import { useCallback } from 'react';

import { Slug } from 'Utilities/profiling/@types';
import { isEnquiryExpirable } from 'Utilities/restricted';
import { Vehicle, VehicleLookupState } from 'Utilities/vehicles/@types';
import { EnquiryOfferResponse } from 'Utilities/vehicles/hooks/useGetEnquiryOffers';
import { useGetEnquiryStatusAndOffers } from 'Utilities/vehicles/hooks/useGetEnquiryStatusAndOffers';
import useGetVehicleEnquiry from 'Utilities/vehicles/hooks/useGetVehicleEnquiry';
import { useSellerQuote } from 'Utilities/vehicles/hooks/useSellerQuote';
import { filterValuations } from 'Utilities/vehicles/trackerValuations';

import { LatestPostSaleOffer, PaymentStatuses } from '../../../payments/Payments.types';

import { checkEnquiryActive } from './useVehicleCard.helpers';
import { useVehicleCardCta } from './useVehicleCardCta';
import { statusesToHideValueTracker } from './VehicleCard.helpers';

type RenderPriceOptions = {
  fallbackValuationPrice?: number | null;
  showEstimateOnLoad?: boolean;
};

export type UseVehicleCardPropsReturn = {
  ctaProps: {
    disabled: boolean;
    label: string;
    loading: boolean;
    to: string;
  };
  displayPrice: string | null;
  estimateData: {
    enquiryOffers?: EnquiryOfferResponse | null;
    enquiryStateSlug?: Slug | null;
    latestPostSaleOfferData?: LatestPostSaleOffer | null;
    paymentsStatuses?: PaymentStatuses | null;
  };
  gaEvent?: string;
  hasPriceRange: boolean;
  hasReservePrice: boolean;
  isBelowOriginalReservePrice?: boolean;
  isEnquiryActive: boolean;
  isEnquiryExpirable: boolean;
  isEnquiryExpired: boolean;
  isOfferRestricted?: boolean;
  isPriceLoading: boolean;
  isSellingButtonLoading: boolean;
  renderPrice: (options?: RenderPriceOptions) => number | null | undefined;
};

export const useVehicleCard = ({ isCVT, vehicle }:
  {isCVT?: boolean; vehicle?: Vehicle | VehicleLookupState}): UseVehicleCardPropsReturn => {
  const {
    enquiryOffers,
    enquiryStateSlug,
    isBelowOriginalReservePrice,
    isLoading: isEnquiryLoading,
    isOfferRestricted,
    latestPostSaleOfferData,
    paymentsStatuses,
  } = useGetEnquiryStatusAndOffers({ vehicle });

  const { enquiry: latestVehicleEnquiry, isLoaded: isLatestVehicleEnquiryLoaded } = useGetVehicleEnquiry({ vehicle });

  const hasExpirableStatus = isEnquiryExpirable(enquiryStateSlug);
  const isEnquiryActive = checkEnquiryActive(vehicle?.enquiry);
  const isEnquiryExpired = hasExpirableStatus && !isEnquiryActive;

  const isSellingButtonLoading = isEnquiryLoading || !isLatestVehicleEnquiryLoaded;

  const hasReservePrice = Boolean(
    latestPostSaleOfferData?.amount
    || enquiryOffers?.closingBid?.value
    || latestVehicleEnquiry?.displayPrice
    || latestVehicleEnquiry?.sellerWillAcceptPrice
    || latestVehicleEnquiry?.closingBidId,
  );

  const trackerValuations = filterValuations(vehicle?.userVehicleTracker?.trackerValuations || []);
  const showValueTracker = !statusesToHideValueTracker.includes(enquiryStateSlug as string) && !isEnquiryActive;
  const showEnquiryEstimate = !(showValueTracker && !!trackerValuations.length);

  const { isLoading: isQuoteLoading, priceRange, rangeDisplayPrice } = useSellerQuote({
    dpEnquiryId: vehicle?.enquiry?.dpEnquiryId,
    enabled: showEnquiryEstimate && !isSellingButtonLoading && !hasReservePrice,
  });
  const displayPrice = hasReservePrice ? null : rangeDisplayPrice;
  const hasPriceRange = !hasReservePrice && Boolean(priceRange);
  const isPriceLoading = isSellingButtonLoading || isQuoteLoading;

  const { gaEvent, label, link } = useVehicleCardCta({
    dpEnquiryId: vehicle?.enquiry?.dpEnquiryId,
    enquiryOffers,
    enquiryStateSlug,
    isBelowOriginalReservePrice,
    isCVT,
    isEnquiryExpired,
    latestPostSaleOfferData,
    paymentsStatuses,
    vrm: vehicle?.vrm,
  });

  const renderPrice = useCallback((options?: RenderPriceOptions) => {
    if (isLatestVehicleEnquiryLoaded) {
      return latestPostSaleOfferData?.amount
        || enquiryOffers?.closingBid?.value
        || latestVehicleEnquiry?.displayPrice
        || latestVehicleEnquiry?.sellerWillAcceptPrice
        || latestVehicleEnquiry?.priceHigh
        || options?.fallbackValuationPrice
        || vehicle?.enquiry?.valuation?.estimate;
    }

    return vehicle?.enquiry?.valuation?.estimate;
  }, [
    isLatestVehicleEnquiryLoaded,
    latestPostSaleOfferData?.amount,
    enquiryOffers?.closingBid?.value,
    latestVehicleEnquiry?.displayPrice,
    latestVehicleEnquiry?.sellerWillAcceptPrice,
    latestVehicleEnquiry?.priceHigh,
    vehicle?.enquiry?.valuation?.estimate,
  ]);

  return {
    ctaProps: {
      disabled: isSellingButtonLoading,
      label,
      loading: isSellingButtonLoading,
      to: link,
    },
    displayPrice,
    estimateData: {
      enquiryOffers,
      enquiryStateSlug,
      latestPostSaleOfferData,
      paymentsStatuses,
    },
    gaEvent,
    hasPriceRange,
    hasReservePrice,
    isBelowOriginalReservePrice,
    isEnquiryActive,
    isEnquiryExpirable: hasExpirableStatus,
    isEnquiryExpired,
    isOfferRestricted,
    isPriceLoading,
    isSellingButtonLoading,
    renderPrice,
  };
};
